import { Component, type ErrorInfo, type ReactNode } from 'react';

import { CrashPanel } from './CrashPanel';
import { reportError } from './error-reporting';

export interface ErrorBoundaryFallbackProps {
    error: unknown;
    componentStack: string | null;
    /** Rendert de kinderen opnieuw. Geen herlaad van de pagina. */
    reset: () => void;
}

export interface ErrorBoundaryProps {
    /** Waar deze grens over gaat: `app`, of de route van het scherm. */
    scope: string;
    /**
     * Verandert een van deze waarden, dan probeert de grens het vanzelf opnieuw.
     * De schil geeft hier de route in mee: navigeren is de tweede uitweg uit een
     * kapot scherm, naast de knop.
     */
    resetKeys?: readonly unknown[];
    fallback?: (props: ErrorBoundaryFallbackProps) => ReactNode;
    children: ReactNode;
}

interface ErrorBoundaryState {
    error: unknown;
    componentStack: string | null;
}

const EMPTY: ErrorBoundaryState = { error: null, componentStack: null };

/**
 * Vangt een fout in de render en zet er een leesbare pagina voor in de plaats.
 *
 * Bewust klein en zonder eigen opmaak: de schil hangt er één om het werkvlak
 * (zodat de rail en de omgevingsaanduiding blijven staan) en één helemaal
 * buitenom, voor het geval de schil zelf omvalt.
 *
 * React biedt hier geen hook voor; een klassecomponent is de enige manier.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    override state: ErrorBoundaryState = EMPTY;

    static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
        return { error };
    }

    override componentDidCatch(error: unknown, info: ErrorInfo): void {
        this.setState({ componentStack: info.componentStack ?? null });

        reportError(error, {
            scope: this.props.scope,
            componentStack: info.componentStack ?? undefined,
        });
    }

    override componentDidUpdate(previous: ErrorBoundaryProps): void {
        if (this.state.error === null || !changed(previous.resetKeys, this.props.resetKeys)) {
            return;
        }

        this.reset();
    }

    reset = (): void => {
        this.setState(EMPTY);
    };

    override render(): ReactNode {
        const { error, componentStack } = this.state;

        if (error === null) {
            return this.props.children;
        }

        if (this.props.fallback) {
            return this.props.fallback({ error, componentStack, reset: this.reset });
        }

        return (
            <CrashPanel
                error={error}
                componentStack={componentStack}
                onRetry={this.reset}
                scope={this.props.scope}
            />
        );
    }
}

function changed(before: readonly unknown[] = [], after: readonly unknown[] = []): boolean {
    return before.length !== after.length || before.some((value, index) => value !== after[index]);
}
